import React, { useEffect, useRef } from "react";

const PERMISSIONS = [
  "can_access",
  "can_create",
  "can_edit",
  "can_delete",
];


const RolePermissionTable = ({
  parentMenu,
  menuList,
  roleMenuAccess,
  rolePermissions,
  onPermissionChange,
  onParentPermissionChange,
  RESTRICTED_PERMISSIONS
}) => {
  /* ===========================
     Column Parent Checkbox State
     =========================== */
  const getParentCheckState = (field) => {
    if (!menuList.length) {
      return { checked: false, indeterminate: false };
    }

    const allChecked = menuList.every(
      (menu) => roleMenuAccess[menu.menu_id]?.[field]
    );

    const someChecked = menuList.some(
      (menu) => roleMenuAccess[menu.menu_id]?.[field]
    );

    return {
      checked: allChecked,
      indeterminate: !allChecked && someChecked,
    };
  };

  /* ===========================
     Parent ALL Permissions State
     =========================== */
  const getParentAllPermissionState = () => {
    if (!menuList.length) {
      return { checked: false, indeterminate: false };
    }

    const allChecked = menuList.every((menu) =>
      PERMISSIONS.every(
        (permission) => roleMenuAccess[menu.menu_id]?.[permission]
      )
    );

    const someChecked = menuList.some((menu) =>
      PERMISSIONS.some(
        (permission) => roleMenuAccess[menu.menu_id]?.[permission]
      )
    );

    return {
      checked: allChecked,
      indeterminate: !allChecked && someChecked,
    };
  };

  /* ===========================
     Indeterminate Ref (React-safe)
     =========================== */
  const parentAllRef = useRef(null);

  useEffect(() => {
    if (parentAllRef.current) {
      parentAllRef.current.indeterminate =
        getParentAllPermissionState().indeterminate;
    }
  });

  /* ===========================
     Render
     =========================== */
  return (
    <div className="table-responsive mb-4">
      <table className="table table-striped table-bordered mb-0">
        <thead>
          {/* ===== Parent ALL Permissions ===== */}
          <tr>
            <th colSpan="5" className="bg-light">
              <div className="d-flex align-items-center justify-content-center gap-2 text-uppercase">
                <input
                  ref={parentAllRef}
                  type="checkbox"
                  className="form-check-input border-primary"
                  checked={getParentAllPermissionState().checked}
                  onChange={(e) =>
                    onParentPermissionChange(
                      parentMenu.menu_id,
                      "ALL",
                      e.target.checked
                    )
                  }
                  disabled={!rolePermissions}
                />
                <span className="fw-bold">
                  {parentMenu.menu_title}
                </span>
              </div>
            </th>
          </tr>

          {/* ===== Column Headers ===== */}
          <tr>
            <th className="w-25">Menu Name</th>

            {PERMISSIONS.map((field) => {
              const { checked, indeterminate } =
                getParentCheckState(field);

              return (
                <th key={field} className="text-center">
                  <input
                    type="checkbox"
                    className="form-check-input me-2 border-primary"
                    ref={(el) => el && (el.indeterminate = indeterminate)}
                    checked={checked}
                    onChange={(e) =>
                      onParentPermissionChange(
                        parentMenu.menu_id,
                        field,
                        e.target.checked
                      )
                    }
                    disabled={!rolePermissions}
                  />
                  {field.replace("can_", "").toUpperCase()}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {menuList.map((menu) => (
            <tr key={menu.menu_id}>
              <td className="fw-bold">{menu.menu_title}</td>

              {PERMISSIONS.map((field) => {
                const restriction = RESTRICTED_PERMISSIONS.find((r) => r.menu_id === Number(menu.menu_id));
                const isRestricted = restriction?.restricted_fields.includes(field);

                return (
                <td key={field} className="text-center">
                  {isRestricted ? (
                    <span className="text-muted fw-bold">-</span>
                  ) : (
                    <input
                      type="checkbox"
                      className="form-check-input border-primary"
                      checked={
                        roleMenuAccess[menu.menu_id]?.[field] || false
                      }
                      onChange={(e) =>
                        onPermissionChange(
                          menu.menu_id,
                          field,
                          e.target.checked
                        )
                      }
                      disabled={!rolePermissions}
                    />
                  )}
                </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RolePermissionTable;
